import type { AudioSourceData } from '@lumen5/luminary';

import type { AudioAssetModel, VideoAssetModel } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    AsyncOperationMessageTypeEnum,
    AsyncOperationNameEnum,
    SourceEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    setAudioEnhancementLoadingForSceneGroup,
    setAudioEnhancementStatusForSceneGroup,
} from 'js/src/data/audio-enhancement/audio-enhancement-actions';
import {
    AUDIO_ENHANCEMENT_ERRORS,
    AUDIO_ENHANCEMENT_TOGGLE_STATUS,
} from 'js/src/data/audio-enhancement/audio-enhancement-types';
import {
    convertBackgroundAssetToFlattenedAsset,
    convertVideoAssetToBackgroundAsset,
    isAudioAsset,
    isAudioSourceData,
    isFlattenedAssetMediaSourceDataCompatible,
    isVideoAsset,
} from 'js/src/data/media';
import { assetContainerModelIsAudio, assetContainerModelIsBackground } from 'js/src/data/uploads';
import { checkpointVideo } from 'js/src/data/video-data';
import { createAbortController } from 'js/src/libs/createAbortController';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isPollAsyncOperationStatusError } from 'js/src/libs/poll_async_operation';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    getVoiceDrivenAsset,
    getVoiceDrivenAssetSourceData,
    isTalkingHeadSceneGroup,
    isVoiceoverSceneGroup,
    saveRawLuminary,
} from 'js/src/luminary';
import { equalWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration } from 'js/src/luminary/voicedriven-scenegroup-utils';
import { runVoicedrivenBatchRun } from 'js/src/luminary/voicedriven-scenes';
import {
    isAsyncOperationEmptyResponse,
    isAsyncOperationResponse,
    isErrorObject,
    OperationAbortedError,
} from 'js/src/types';

export const silentlyChangeVoiceDrivenAssetSourceDataOperation = createLuminaryOperation(
    async (
        { luminary, dispatch },
        sceneGroupId: string,
        asset: AudioAssetModel | AudioSourceData | VideoAssetModel,
        systemTriggered = true,
        parentActionGroup = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        // Resolve which asset slot owns the swap and capture the narrowed sourceData in the
        // closure. Doing this up-front (before any awaits / batches) keeps the swap-and-fix flow
        // below uniform across talking-head vs voiceover scene groups.
        let applySourceData: (() => void) | undefined;
        if (isTalkingHeadSceneGroup(sceneGroup) && isVideoAsset(asset)) {
            const backgroundAsset = convertVideoAssetToBackgroundAsset(asset);
            const flattened = convertBackgroundAssetToFlattenedAsset(backgroundAsset);
            if (isFlattenedAssetMediaSourceDataCompatible(flattened)) {
                applySourceData = () => {
                    sceneGroup.mediaAsset.setSourceDataWithoutResettingProperties(flattened);
                };
            }
        } else if (
            isVoiceoverSceneGroup(sceneGroup) &&
            (isAudioAsset(asset) || isAudioSourceData(asset))
        ) {
            const audioSourceData = asset;
            applySourceData = () => {
                sceneGroup.voiceoverAsset.setSourceDataWithoutResettingProperties(audioSourceData);
            };
        }

        if (!applySourceData) {
            throw Error(
                'Could not set asset source data - object passed in was incompatible with scenegroup',
            );
        }

        await luminary.onAllSlideImagesRendered();
        if (sceneGroup.destroyed) {
            return;
        }

        // Snapshot whether the asset was "fully covered" by its playback region BEFORE we swap
        // sourceData. We use this after the new asset loads to decide whether to extend the
        // playback region (`uncut`) to cover the new asset's duration.
        let assetEndWithinPlaybackRegion = false;
        runVoicedrivenBatchRun(luminary, sceneGroupId, () => {
            const voicedrivenAsset = getVoiceDrivenAsset(sceneGroup);
            if (
                !voicedrivenAsset ||
                voicedrivenAsset.sourceDuration === null ||
                voicedrivenAsset.endTime === null
            ) {
                throw new Error('Media asset source duration or end time is null');
            }
            assetEndWithinPlaybackRegion = equalWithinEpsilon(
                voicedrivenAsset.sourceDuration,
                voicedrivenAsset.endTime,
            );
            applySourceData!();
            synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration(sceneGroup);
        });

        // Wait for the new asset to actually load so we have the real sourceDuration, then
        // reconcile any drift + optionally uncut, both inside one batch.
        await sceneGroup.root.onCanPlay();
        if (sceneGroup.destroyed) {
            return;
        }

        runVoicedrivenBatchRun(luminary, sceneGroupId, () => {
            const voicedrivenAsset = getVoiceDrivenAsset(sceneGroup);
            if (!voicedrivenAsset) {
                return;
            }
            if (voicedrivenAsset.sourceDuration === null || voicedrivenAsset.endTime === null) {
                throw new Error('Media asset source duration or end time is null');
            }
            if (
                assetEndWithinPlaybackRegion &&
                voicedrivenAsset.sourceDuration !== voicedrivenAsset.endTime
            ) {
                // Luminary was not able to correct the playback region, we must fix it.
                if (voicedrivenAsset.sourceDuration > voicedrivenAsset.endTime) {
                    voicedrivenAsset.uncut(
                        voicedrivenAsset.endTime,
                        voicedrivenAsset.sourceDuration,
                    );
                } else {
                    ErrorReporter.setContext('audio-enhancement-error', {
                        voicedrivenAssetSourceDuration: voicedrivenAsset.sourceDuration,
                        voicedrivenAssetEndTime: voicedrivenAsset.endTime,
                    });
                    throw new Error(
                        "Asset endTime cannot be greater than sourceDuration - luminary's MediaAsset should have auto-corrected this!",
                    );
                }
            }
            synchronouslyUpdateSceneGroupDurationToEqualVoiceDrivenAssetDuration(sceneGroup);
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);

export const startAudioEnhancementOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState, call }, sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);

        if (!sourceData) {
            return;
        }

        let asset_type: string, asset_source_id: string | null;
        if (isAudioSourceData(sourceData)) {
            asset_type = AssetContainerTypes.AUDIO;
            asset_source_id = sourceData.source_id;
        } else {
            asset_type = sourceData.type;
            asset_source_id = sourceData.source_id;
        }
        if (!asset_source_id) {
            throw new Error(
                'In startAudioEnhancementOperation, asset_source_id is required but was not provided.',
            );
        }

        const target_asset_source = SourceEnum.SOURCE_AUDIO_ENHANCEMENT;
        const abortController = createAbortController();

        dispatch(
            setAudioEnhancementLoadingForSceneGroup({
                sceneGroupId,
                isLoading: true,
                abortController,
            }),
        );
        // optimistically turn it on
        dispatch(
            setAudioEnhancementStatusForSceneGroup({
                sceneGroupId,
                status: AUDIO_ENHANCEMENT_TOGGLE_STATUS.ON,
            }),
        );

        let t0;
        let error;
        try {
            const response = await ApiService.assetAudioEnhancementRetrieve({
                query: {
                    asset_source_id,
                    asset_type,
                    target_asset_source,
                },
            });

            if (isAsyncOperationEmptyResponse(response.data)) {
                return;
            }

            let assetData: VideoAssetModel | AudioAssetModel | undefined;
            if (isAsyncOperationResponse(response.data)) {
                t0 = performance.now();
                const ws = WebSocketClient.getInstance();
                assetData = await ws.waitForMessage<VideoAssetModel | AudioAssetModel>(
                    response.data.async_operation_uuid,
                    AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT,
                    AsyncOperationNameEnum.ASYNC_ENHANCE_AUDIO,
                    { abortController },
                );
            } else {
                if (assetContainerModelIsBackground(response.data)) {
                    const videoAsset = response.data.background.video;
                    if (!videoAsset) {
                        throw new Error(
                            'When starting audio enhancement: expected video asset, but got null',
                        );
                    }
                    assetData = videoAsset;
                } else if (assetContainerModelIsAudio(response.data)) {
                    assetData = response.data.audio;
                }
            }

            if (!assetData) {
                throw new Error(
                    'When starting audio enhancement: expected asset data, but got undefined',
                );
            }

            call(silentlyChangeVoiceDrivenAssetSourceDataOperation, sceneGroupId, assetData);
            dispatch(setAudioEnhancementLoadingForSceneGroup({ sceneGroupId, isLoading: false }));
        } catch (e: unknown) {
            error = e;
            const userCanceled = isErrorObject(e) && e.error instanceof OperationAbortedError;
            if (!userCanceled) {
                Toast.error(AUDIO_ENHANCEMENT_ERRORS.ENHANCEMENT_FAILED);
            }
            dispatch(setAudioEnhancementLoadingForSceneGroup({ sceneGroupId, isLoading: false }));
            dispatch(
                setAudioEnhancementStatusForSceneGroup({
                    sceneGroupId,
                    status: AUDIO_ENHANCEMENT_TOGGLE_STATUS.OFF,
                }),
            );
        } finally {
            if (t0) {
                const t1 = performance.now();
                error = isErrorObject(error) ? error.error : error;
                analytics.track('WebSocket Audio Enhancement Performance', {
                    pollingDuration: t1 - t0,
                    error: !!error && !(error instanceof OperationAbortedError),
                    errorString: isPollAsyncOperationStatusError(error)
                        ? error.errorThrown
                        : error instanceof Error
                          ? error.message
                          : JSON.stringify(error),
                });
            }
        }
    },
);

export const loadUnenhancedAssetOperation = createLuminaryOperation(
    async ({ luminary, dispatch, getState, call }, sceneGroupId: string) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const sourceData = getVoiceDrivenAssetSourceData(sceneGroup);
        if (!sourceData) {
            return;
        }

        let asset_type: string, asset_source_id: string | null, target_asset_source: SourceEnum;
        if (isAudioSourceData(sourceData)) {
            asset_type = 'audio';
            asset_source_id = sourceData.source_id;
            target_asset_source = SourceEnum.SOURCE_USER_VOICEOVER;
        } else {
            asset_type = sourceData.type;
            asset_source_id = sourceData.source_id;
            target_asset_source = SourceEnum.SOURCE_USER;
        }

        if (!asset_source_id) {
            throw new Error(
                'In loadUnenhancedAssetOperation, asset_source_id is required but was not provided.',
            );
        }

        dispatch(setAudioEnhancementLoadingForSceneGroup({ sceneGroupId, isLoading: true }));
        // optimistically turn it off
        dispatch(
            setAudioEnhancementStatusForSceneGroup({
                sceneGroupId,
                status: AUDIO_ENHANCEMENT_TOGGLE_STATUS.OFF,
            }),
        );

        try {
            const response = await ApiService.assetAudioEnhancementRetrieve({
                query: {
                    asset_source_id,
                    asset_type,
                    target_asset_source,
                },
            });
            if (
                isAsyncOperationResponse(response.data) ||
                isAsyncOperationEmptyResponse(response.data)
            ) {
                throw new Error(
                    'When loading unenhanced asset: expected asset data, but got async operation response',
                );
            }
            let assetData: VideoAssetModel | AudioAssetModel | undefined;
            if (assetContainerModelIsBackground(response.data)) {
                const videoAsset = response.data.background.video;
                if (!videoAsset) {
                    throw new Error(
                        'When loading unenhanced asset: expected video asset, but got null',
                    );
                }
                assetData = videoAsset;
            } else if (assetContainerModelIsAudio(response.data)) {
                assetData = response.data.audio;
            }

            if (!assetData) {
                throw new Error(
                    'When loading unenhanced asset: expected asset data, but got undefined',
                );
            }

            call(silentlyChangeVoiceDrivenAssetSourceDataOperation, sceneGroupId, assetData);
            dispatch(setAudioEnhancementLoadingForSceneGroup({ sceneGroupId, isLoading: false }));
        } catch (error: unknown) {
            Toast.error(AUDIO_ENHANCEMENT_ERRORS.LOAD_ORIGINAL_ASSET_FAILED);
            dispatch(setAudioEnhancementLoadingForSceneGroup({ sceneGroupId, isLoading: false }));
            dispatch(
                setAudioEnhancementStatusForSceneGroup({
                    sceneGroupId,
                    status: AUDIO_ENHANCEMENT_TOGGLE_STATUS.ON,
                }),
            );
        }
    },
);
